import { useEffect, useState } from 'react';
import { deriveCoverTint } from '../utils/coverTint';
import { isVideoCover, representativeFrameTime } from '../utils/shopCover';

/**
 * How long to wait for a seek before sampling whatever frame is decoded.
 *
 * A `seeked` event is not guaranteed: a stream that cannot seek, a partially
 * buffered clip, or a browser that decides the seek was a no-op all leave the
 * promise unkept. Without this the page sits on the theme-colour fallback
 * forever, which is precisely the bug this path exists to fix — so the timeout
 * samples the frame that IS decoded rather than giving up.
 */
export const SEEK_TIMEOUT_MS = 4000;

/**
 * Derives a soft page tint from the shop's cover (see `utils/coverTint.js` for
 * the pixel math). Returns `null` — the signal to the caller "use the theme
 * colour instead" — in every case that is not a clean, same-origin-enough
 * load: no URL at all, the media failing to load, or `deriveCoverTint`
 * catching a tainted canvas. The caller renders the theme-colour tint
 * unconditionally underneath and layers this on top only once/if it resolves,
 * so there is never a moment with no tint at all.
 *
 * TWO SOURCES, ONE SAMPLER. A cover can be a photograph or a video, and
 * `ShopHero` has rendered both since round 1 — but this hook only knew about
 * images, so it loaded an `.mp4` into an `Image`, `onerror` fired, and a shop
 * with a video cover was coloured as though it had no cover at all. Both paths
 * end in the same `deriveCoverTint(element)`, because a canvas will draw
 * either one; what differs is only how you wait for a frame to exist.
 */
export default function useCoverTint(imageUrl) {
  const [tint, setTint] = useState(null);

  useEffect(() => {
    if (!imageUrl || typeof window === 'undefined') {
      setTint(null);
      return undefined;
    }
    let cancelled = false;

    if (isVideoCover(imageUrl)) {
      const video = document.createElement('video');
      // Same request as the image path: CORS-friendly pixels, and if the host
      // does not answer with the headers the canvas taints and
      // `deriveCoverTint` catches it.
      video.crossOrigin = 'anonymous';
      // Muted + inline + metadata-first so this costs a frame, not playback.
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      let timer = null;
      let done = false;

      const settle = () => {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        if (!cancelled) setTint(deriveCoverTint(video));
      };
      const onSeeked = () => settle();
      const onLoaded = () => {
        if (cancelled || done) return;
        // `loadeddata`, not `load`: a video never fires `load`, which is why
        // the image path answered null for every clip.
        video.addEventListener('seeked', onSeeked, { once: true });
        timer = setTimeout(settle, SEEK_TIMEOUT_MS);
        try {
          video.currentTime = representativeFrameTime(video.duration);
        } catch (_err) {
          settle();
        }
      };
      const onError = () => {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        if (!cancelled) setTint(null);
      };

      video.addEventListener('loadeddata', onLoaded, { once: true });
      video.addEventListener('error', onError, { once: true });
      video.src = imageUrl;
      if (typeof video.load === 'function') video.load();

      return () => {
        cancelled = true;
        done = true;
        if (timer) clearTimeout(timer);
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        // Stop the fetch: a detached <video> with a src keeps downloading.
        video.removeAttribute('src');
        if (typeof video.load === 'function') video.load();
      };
    }

    if (typeof Image === 'undefined') {
      setTint(null);
      return undefined;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      setTint(deriveCoverTint(img));
    };
    img.onerror = () => {
      if (!cancelled) setTint(null);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return tint;
}
